import { push } from 'connected-react-router'
import { addDays, endOfMonth, format, isSameDay, startOfMonth } from 'date-fns'
import { State } from 'index'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styles from 'styles/components/template.module.scss'
import { BlueButton, NavButton } from 'components/Button'
import { StockCalendarContainer } from 'components/Calendar'
import { Description, MainHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { useIsAfterLastOrder } from 'hooks/store'
import { useEarliestBusinessTime } from 'hooks/businessHour'
import { useGetTemporaryOpen } from 'hooks/storeCalendar'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { dateToString, parseTimeToDate } from 'lib/helper'
import { getStoreRef } from 'reducks/auth/selectors'
import { fetchClosedDates } from 'reducks/calendar/operations'
import { getClosedDates, getSpecialHolidays } from 'reducks/calendar/selectors'
import { getHolidays, getLastOrderTime } from 'reducks/store/selectors'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { BusinessTimeType } from 'types/store'
import useNextBusinessDate from '../../../hooks/store/useNextBusinessDate'
const daysOfWeek = new DaysOfWeek()

const StockTemplate: FC = () => {
  const now = useMemo(() => new Date(), [])

  const dispatch = useDispatch()
  const closedDates = useSelector((state: State) => getClosedDates(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const lastOrderTime: BusinessTimeType = useSelector((state: State) => getLastOrderTime(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))

  const [currentDate, setCurrentDate] = useState<Date>(addDays(now, 1))
  const [currentMonth, setCurrentMonth] = useState<Date>(addDays(now, 1))
  const handleCurrentDate = useCallback((date: Date) => setCurrentDate(date), [setCurrentDate])
  const handleCurrentMonth = useCallback(
    (date: Date) => {
      setCurrentDate(date)
      setCurrentMonth(date)
    },
    [setCurrentDate, setCurrentMonth]
  )

  // ラストオーダー以降かどうかを判定
  const dayOfWeek: DayOfWeekType = daysOfWeek.getDayOfWeek(now, specialHolidays)
  const temporaryOpenCalendar = useGetTemporaryOpen(dateToString(now), storeCalendars)
  const earliestLastOrderTime = useEarliestBusinessTime(
    [dayOfWeek],
    lastOrderTime,
    'last_order_time',
    temporaryOpenCalendar
  )
  const lastOrderDateTime = parseTimeToDate(earliestLastOrderTime)
  const isAfterLastOrder = useIsAfterLastOrder(now, lastOrderDateTime)
  const isClosed = useMemo(() => closedDates.some((closedDate) => isSameDay(closedDate, now)), [closedDates, now])
  const nextBusinessDate = useNextBusinessDate(closedDates)

  // 月が変わったら休業日を取得する
  useEffect(() => {
    if (!storeRef) return
    dispatch(
      fetchClosedDates(startOfMonth(currentMonth), endOfMonth(currentMonth), holidays, specialHolidays, storeRef)
    )
  }, [currentMonth, holidays, storeRef, specialHolidays])

  // 月が変わったら臨時営業日・休業日を取得する
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(currentMonth)))
  }, [storeRef, currentMonth])

  // 翌日が休業日の場合、翌営業日を選択した状態にする
  useEffect(() => {
    const tomorrow = addDays(now, 1)
    if (closedDates.some((closedDate) => isSameDay(closedDate, tomorrow))) {
      setCurrentDate(nextBusinessDate)
    }
  }, [closedDates, nextBusinessDate])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <Description description={'在庫を編集したい日にちを選択してください'} />
      <Spacer size={20} />
      <MainHeadline headline={'本日の在庫を編集する'} />
      <Spacer size={20} />
      <BlueButton
        disabled={isClosed || isAfterLastOrder}
        label={
          isClosed || isAfterLastOrder
            ? `本日(${format(now, 'MM月dd日')})の営業は終了しました`
            : `本日(${format(now, 'MM月dd日')})の在庫を編集する`
        }
        onClick={() => dispatch(push('/stock/released'))}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={20} />
      <MainHeadline headline={'明日以降の在庫を編集する'} />
      <Spacer size={20} />
      <Description
        description={'明日以降の在庫を編集したい場合、カレンダーから日にちを選択して、次のページに移動してください'}
      />
      <Spacer size={20} />
      <StockCalendarContainer
        currentDate={currentDate}
        currentMonth={currentMonth}
        handleCurrentDate={handleCurrentDate}
        handleCurrentMonth={handleCurrentMonth}
      />
      <Spacer size={20} />
      <BlueButton
        label={`${format(currentDate, 'yyyy年MM月dd日')}の在庫を編集する`}
        onClick={() => dispatch(push(`/scheduled_item/${format(currentDate, 'yyyyMMdd')}`))}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
    </div>
  )
}

export default StockTemplate
